'use client';

import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthProvider';
import { ProfileProvider } from '@/contexts/ProfileProvider';
import type { AuthSnapshot } from '@/lib/auth';
import type { UserProfileSnapshot } from '@/lib/profile';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { CookiesProvider } from 'react-cookie';

interface ProvidersProps {
  initialAuth?: AuthSnapshot;
  initialProfile?: UserProfileSnapshot;
  children: React.ReactNode;
}

export default function Providers({ initialAuth, initialProfile, children }: ProvidersProps) {
  const [queryClient] = useState(() => new QueryClient());

  // The New Relic browser agent has import-time side effects that crash during
  // server rendering, so it must only ever be loaded in the browser.
  useEffect(() => {
    void import('@/newrelic');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CookiesProvider>
        <AuthProvider initialAuth={initialAuth}>
          <ProfileProvider initialProfile={initialProfile}>
            {children}
            <Toaster />
          </ProfileProvider>
        </AuthProvider>
      </CookiesProvider>
    </QueryClientProvider>
  );
}
